import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Pause, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ConversationAudioProps } from './types';
import { isDualTrack } from './types';
import { leadInWidthPercent, pickLeader, ringLeadIn, trackTargetSeconds } from './audio-sync';
import { formatDuration, formatPlaybackRate } from './format';

/**
 * Sticky audio controls at the bottom of the conversation view.
 *
 * Two playback modes:
 *
 *   - **Mixed** (`src` provided): single `<audio>` element drives both
 *     sides of the conversation.
 *
 *   - **Dual-track** (`callerSrc` + `agentSrc`): two `<audio>` elements
 *     rendered in lockstep by a shared timeline. We pick whichever
 *     track has the smaller offset as the leader (its sample at
 *     position 0 is closest to `startedAt`); the follower's
 *     `currentTime` is `max(0, leaderMs - followerOffsetMs) / 1000`.
 *     Both `callerOffsetMs` and `agentOffsetMs` are honored.
 *
 * `preload="metadata"` on every element, deliberately — see `durationMs`
 * in `./types` for what `"none"` costs.
 *
 * `className` overrides the default foot-of-panel chrome. The sheet renders
 * this bar ABOVE the transcript instead of inside it, where a reader can
 * actually see it, so it needs a full border rather than a top edge.
 */
export function AudioBar({
  audio,
  className,
  playbackRates,
}: {
  audio: ConversationAudioProps;
  className?: string;
  /**
   * Speeds offered in the rate menu, in the order they are listed. Omit — the
   * default — and no rate control is drawn at all, which is right for a live
   * call, where there is nothing to speed up.
   *
   * Playback always STARTS at 1×, whatever this list contains; the first entry
   * is not a default. A recording that began at 1.5× because that happened to
   * be first in an array would be a surprising thing for a sheet to do.
   */
  playbackRates?: number[];
}): React.ReactElement {
  const { isPlaying, onPlayingChange, currentMs, onSeek } = audio;
  const leaderRef = useRef<HTMLAudioElement | null>(null);
  const followerRef = useRef<HTMLAudioElement | null>(null);
  const [measuredDurationMs, setMeasuredDurationMs] = useState<number | null>(null);
  const [rate, setRate] = useState(1);

  const rates = playbackRates?.length ? playbackRates : null;

  const dual = isDualTrack(audio);
  const roles = dual ? pickLeader(audio) : null;

  // Drop the measured length the moment the source changes. The bar is not
  // remounted when the sheet is re-pointed at another call, and the measured
  // value OUTRANKS the server's — so without this the readout and the
  // scrubber's `max` stay on the previous recording's length until the new
  // `loadedmetadata` fires, and a drag past the new (shorter) end seeks
  // beyond the file.
  const leadingSrc = dual ? roles?.leaderSrc : audio.src;
  useEffect(() => {
    setMeasuredDurationMs(null);
  }, [leadingSrc]);

  // The measured length wins once we have it; the server's value carries the
  // bar until then so the readout and scrubber are live on first paint.
  const durationMs = measuredDurationMs ?? audio.durationMs ?? null;

  // The silent pre-pickup head, as a share of the track. Null on everything
  // without both instants — which is most calls — and the shade then does not
  // render at all rather than rendering at zero width. Sized against the
  // MEASURED duration where we have it, so the shade and the thumb agree about
  // how long the file is.
  const leadInPercent = (() => {
    const leadIn = ringLeadIn(audio.startedAt, audio.answeredAt, {
      durationMs: durationMs ?? undefined,
    });
    return leadIn ? leadInWidthPercent(leadIn.durationMs, durationMs) : null;
  })();

  // `playbackRate` is element state, not attribute state — React will not set
  // it for us and a fresh `<audio>` starts at 1. Re-applied on `leadingSrc` as
  // well as on `rate`, because pointing the bar at another call swaps the
  // element's source and a mid-review 1.5× would silently drop back to 1×.
  // BOTH tracks, or a dual-track call desynchronises the moment you speed up.
  useEffect(() => {
    if (leaderRef.current) leaderRef.current.playbackRate = rate;
    if (followerRef.current) followerRef.current.playbackRate = rate;
  }, [rate, leadingSrc, dual]);

  // Drive both tracks when the parent controls currentMs.
  useEffect(() => {
    if (currentMs == null) return;
    seekElement(leaderRef.current, currentMs, roles?.leaderOffsetMs ?? 0);
    if (dual) {
      seekElement(followerRef.current, currentMs, roles?.followerOffsetMs ?? 0);
    }
  }, [currentMs, dual, roles?.leaderOffsetMs, roles?.followerOffsetMs]);

  const toggle = (): void => {
    const leader = leaderRef.current;
    if (!leader) return;
    if (isPlaying) {
      leader.pause();
      followerRef.current?.pause();
      onPlayingChange?.(false);
    } else {
      void leader.play();
      if (dual) void followerRef.current?.play();
      onPlayingChange?.(true);
    }
  };

  const handleLeaderTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>): void => {
    // Leader's currentTime is offset-relative to its own track's first sample.
    // Convert to absolute (relative to startedAt) before broadcasting.
    const leaderSec = e.currentTarget.currentTime ?? 0;
    const ms = leaderSec * 1000 + (roles?.leaderOffsetMs ?? 0);
    onSeek?.(ms);
    if (dual) {
      seekElement(followerRef.current, ms, roles?.followerOffsetMs ?? 0);
    }
  };

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>): void => {
    const dur = (e.currentTarget.duration ?? 0) * 1000 + (roles?.leaderOffsetMs ?? 0);
    // A streamed Ogg whose tail the browser couldn't range-request reports
    // Infinity. Leaving `measured` null then falls back to the server's
    // duration rather than rendering "Infinity" or a dead 0.
    setMeasuredDurationMs(Number.isFinite(dur) && dur > 0 ? dur : null);
  };

  return (
    <div className={cn('flex items-center gap-3 border-t bg-background/95 px-4 py-2', className)}>
      {dual && roles ? (
        <>
          <audio
            ref={leaderRef}
            src={roles.leaderSrc}
            preload="metadata"
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleLeaderTimeUpdate}
            onEnded={() => onPlayingChange?.(false)}
            className="hidden"
          />
          <audio ref={followerRef} src={roles.followerSrc} preload="metadata" className="hidden" />
        </>
      ) : (
        <audio
          ref={leaderRef}
          src={audio.src}
          preload="metadata"
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleLeaderTimeUpdate}
          onEnded={() => onPlayingChange?.(false)}
          className="hidden"
        />
      )}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="h-8 w-8 shrink-0 rounded-full p-0"
        onClick={toggle}
        aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
      >
        {isPlaying ? (
          <Pause className="h-3.5 w-3.5" fill="currentColor" />
        ) : (
          <Play className="h-3.5 w-3.5" fill="currentColor" />
        )}
      </Button>
      <div className="relative flex flex-1 items-center">
        {/* THE RING, SHOWN AS A DIFFERENT KIND OF TIME. An outbound recording
            starts at egress, while the line is still ringing, and that stretch
            is silent in the mix — so without this the bar's head looks like a
            dead agent rather than a ringing phone. Decorative and inert: the
            transcript's ring row states the number in text and owns the
            click, so this must not intercept a drag toward position zero.
            Width is a runtime proportion no utility class can express — the
            inline-style exception, same as the React Flow edge labels in
            `pages/setup/workflows/Canvas.tsx`. */}
        {leadInPercent !== null && (
          <span
            data-ring-lead-in-shade="true"
            aria-hidden="true"
            className="pointer-events-none absolute left-0 h-1 rounded-l-full bg-foreground/20"
            style={{ width: `${leadInPercent}%` }}
          />
        )}
        <input
          type="range"
          min={0}
          max={durationMs ?? 0}
          step={100}
          value={Math.min(currentMs ?? 0, durationMs ?? 0)}
          onChange={(e) => onSeek?.(Number(e.target.value))}
          disabled={durationMs === null}
          className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-muted accent-primary disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Audio position"
        />
      </div>
      <span className="shrink-0 tabular-nums text-xs text-muted-foreground">
        {formatDuration(currentMs ?? 0)}
        {durationMs != null && ` / ${formatDuration(durationMs)}`}
      </span>
      {rates && (
        // A MENU, not a button that cycles. The first cut relabelled itself on
        // each click, which never says what the other speeds are or that there
        // are any — the reader has to press it and watch to find out, and
        // overshooting means going all the way round. The menu shows the whole
        // range at once and marks the current one, so picking 1.5× is one aimed
        // click instead of two blind ones.
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 gap-0.5 px-1.5 font-mono text-xs tabular-nums text-muted-foreground"
              aria-label={`Playback speed, currently ${formatPlaybackRate(rate)}`}
            >
              {formatPlaybackRate(rate)}
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          {/* `align="end"` keeps the menu inside the sheet — the control sits
              at the right edge of the bar, and a centred menu would open past
              it. Radix flips the side on its own if the bar is near the
              viewport's bottom. */}
          <DropdownMenuContent align="end" className="min-w-32">
            <DropdownMenuLabel className="text-xs">Playback speed</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={String(rate)}
              onValueChange={(value) => setRate(Number(value))}
            >
              {rates.map((option) => (
                <DropdownMenuRadioItem
                  key={option}
                  value={String(option)}
                  className="font-mono text-xs tabular-nums"
                >
                  {formatPlaybackRate(option)}
                  {/* The speed nobody thinks of as a speed. Naming it means the
                      way back to normal is findable without knowing that 1×
                      is what normal is called. */}
                  {option === 1 && (
                    <span className="ml-1.5 font-sans text-muted-foreground">Normal</span>
                  )}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function seekElement(el: HTMLAudioElement | null, absoluteMs: number, offsetMs: number): void {
  if (!el) return;
  const targetSec = trackTargetSeconds(absoluteMs, offsetMs);
  if (Math.abs(el.currentTime - targetSec) > 0.25) {
    el.currentTime = targetSec;
  }
}
